import { ApiError, type CodHandover } from "@khazzin/api-client";
import { formatDateTime, formatTime } from "@khazzin/i18n/dates";
import { formatMoney } from "@khazzin/i18n/money";
import { StatusChip } from "@khazzin/ui/patterns/StatusChip";
import { Button } from "@khazzin/ui/primitives/Button";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router";
import { useApi } from "../../api";
import { timeZone } from "../../app/shell/Shell";
import { useI18n } from "../../i18n";
import { useOffline } from "../../offline/Offline";
import { cashRows } from "../cod/cod";
import { HandoverQr } from "./HandoverQr";
import {
	currencyCards,
	HANDOVERS_KEY,
	type Phase,
	phaseOf,
	queuedCod,
	startGate,
	tallies,
} from "./handover";

/** How often the screen asks the desk's answer while the QR is shown. */
export const POLL_MS = 10_000;

const PHASE_TONE = { waiting: "info", confirmed: "success", disputed: "warning" } as const;

/** What a refused Start says. A queued confirmation synced since is refused as unsynced too. */
const REFUSALS: Record<string, string> = {
	nothing_to_hand_over: "driver.handover.empty",
	handover_open: "driver.handover.alreadyOpen",
	unsynced_collections: "driver.handover.syncFirst",
};

/**
 * The day-end handover (DA-10-21, EP10-S20), from Cash → End of day: the
 * day's tallies, one card per currency, and **Start handover**, which puts up
 * everything the driver holds for the desk and shows its QR and six digits.
 * The screen then waits for the cashier — the stream's hint, or a poll every
 * ten seconds — and turns Confirmed (the wallet re-reads and drops the cash)
 * or Disputed (the cash stays in the wallet). Nothing starts while a cash
 * confirmation is still queued: the batch would leave it out.
 */
export function HandoverSummary() {
	const { t, locale } = useI18n();
	const { day, online, wallet, outbox, syncing, syncNow, tenderOptions } = useOffline();
	const api = useApi();
	const client = useQueryClient();
	// The handover on screen: the one just started, or the one found waiting
	// at the desk. Kept once seen, so its answer stays up after it closes.
	const [currentId, setCurrentId] = useState<string | null>(null);
	const history = useQuery({
		queryKey: HANDOVERS_KEY,
		queryFn: ({ signal }) => api.listMyCodHandovers({ signal }),
		enabled: online,
	});
	const open = history.data?.items.find((h) => phaseOf(h) === "waiting");
	useEffect(() => {
		if (currentId === null && open !== undefined) {
			setCurrentId(open.handoverId);
		}
	}, [currentId, open]);
	const current = useQuery({
		queryKey: [...HANDOVERS_KEY, currentId],
		queryFn: ({ signal }) => api.getMyCodHandover(currentId as string, { signal }),
		enabled: online && currentId !== null,
		refetchInterval: (q) => (q.state.data && phaseOf(q.state.data) !== "waiting" ? false : POLL_MS),
	});
	const shown = current.data;
	const phase: Phase | null = shown ? phaseOf(shown) : null;
	// The desk answered: the wallet re-reads (a confirmed handover leaves it) and so does the history.
	// Once per answer: `syncNow` is a new function on every render.
	const answered = useRef<string | null>(null);
	useEffect(() => {
		const key = `${currentId}:${phase}`;
		if ((phase === "confirmed" || phase === "disputed") && answered.current !== key) {
			answered.current = key;
			syncNow();
			void client.invalidateQueries({ queryKey: HANDOVERS_KEY, exact: true });
		}
	}, [currentId, phase, syncNow, client]);

	const start = useMutation({
		mutationFn: () =>
			api.initiateCodBatch({
				clientEventKey: `handover-${crypto.randomUUID()}`,
				collectionIds: [],
				queuedCodCount: queuedCod(outbox),
			}),
		onSuccess: (h) => {
			client.setQueryData([...HANDOVERS_KEY, h.handoverId], h);
			setCurrentId(h.handoverId);
			void client.invalidateQueries({ queryKey: HANDOVERS_KEY, exact: true });
		},
		onError: (err) => {
			if (err instanceof ApiError && err.code === "handover_open") {
				void client.invalidateQueries({ queryKey: HANDOVERS_KEY, exact: true });
			}
		},
	});

	const rows = cashRows(wallet, outbox, day?.shipments ?? {}, tenderOptions);
	const counts = tallies(day?.shipments ?? {}, outbox);
	const queued = queuedCod(outbox);
	const refused =
		start.error instanceof ApiError ? start.error.code : start.error ? "failed" : null;
	const gate = startGate(queued, online, rows.filter((r) => !r.queued).length, phase === "waiting");
	const money = (amountMinor: number, currency: string) =>
		formatMoney({ amountMinor, currency }, locale);

	return (
		<div className="flex flex-col gap-4">
			<Link to="/cash" className="min-h-12 content-center text-[14px] font-bold text-fg">
				{t("driver.handover.back")}
			</Link>
			<h1 className="text-[20px] font-bold text-fg">{t("driver.handover.title")}</h1>

			<dl className="grid grid-cols-3 gap-2 sm:grid-cols-5">
				{(["delivered", "partial", "failed", "pickups", "queued"] as const).map((k) => (
					<div key={k} className="card p-3">
						<dt className="text-[12px] text-fg-muted">{t(`driver.handover.tally.${k}`)}</dt>
						<dd className="text-[22px] font-bold text-fg">{counts[k]}</dd>
					</div>
				))}
			</dl>

			{(currencyCards(rows).length === 0
				? [{ currency: "USD", expectedMinor: 0, collectedMinor: 0, varianceMinor: 0, count: 0 }]
				: currencyCards(rows)
			).map((c) => (
				<section key={c.currency} aria-label={c.currency} className="flex flex-col gap-2 card p-4">
					<h2 className="text-[15px] font-bold text-fg">
						{t("driver.handover.card", { currency: c.currency, count: c.count })}
					</h2>
					<dl className="grid grid-cols-[1fr_auto] gap-x-4 gap-y-1 text-[14px]">
						<dt className="text-fg-muted">{t("driver.handover.expected")}</dt>
						<dd className="text-end text-fg">{money(c.expectedMinor, c.currency)}</dd>
						<dt className="text-fg-muted">{t("driver.handover.collected")}</dt>
						<dd className="text-end font-bold text-fg">{money(c.collectedMinor, c.currency)}</dd>
						<dt className="text-fg-muted">{t("driver.handover.variance")}</dt>
						<dd className="text-end text-fg">{money(c.varianceMinor, c.currency)}</dd>
					</dl>
				</section>
			))}

			{queued > 0 && (
				<section
					role="alert"
					className="flex flex-col gap-2 rounded-lg border border-warning-border bg-warning-bg p-4 text-warning-fg"
				>
					<h2 className="text-[15px] font-bold">{t("driver.handover.syncFirst")}</h2>
					<p className="text-[14px]">{t("driver.handover.syncFirstBody", { count: queued })}</p>
					<Button size="lg" loading={syncing} disabled={!online} onClick={syncNow}>
						{t("driver.handover.syncNow")}
					</Button>
				</section>
			)}

			{shown && phase === "waiting" && (
				<section className="flex flex-col items-center gap-3 card p-4">
					<HandoverQr payload={shown.handoverId} code={shown.shortCode} />
					<p className="text-center text-[14px] text-fg">
						{t("driver.handover.show", { amount: money(shown.expectedTotalMinor, shown.currency) })}
					</p>
					<StatusChip tone="info">{t("driver.handover.waiting")}</StatusChip>
				</section>
			)}
			{shown && phase === "confirmed" && (
				<p
					role="status"
					className="rounded-lg border border-success-border bg-success-bg p-4 text-[15px] font-bold text-success-fg"
				>
					{t("driver.handover.confirmed", {
						cashier: shown.countedByName ?? t("driver.handover.cashier"),
						time: formatTime(shown.countedAt ?? shown.closedAt ?? shown.createdAt, {
							locale,
							timeZone,
						}),
					})}
				</p>
			)}
			{shown && phase === "disputed" && (
				<section
					role="status"
					className="flex flex-col gap-1 rounded-lg border border-warning-border bg-warning-bg p-4 text-warning-fg"
				>
					<h2 className="text-[15px] font-bold">{t("driver.handover.disputed")}</h2>
					<p className="text-[14px]">
						{t("driver.handover.disputedNote", {
							amount: money(shown.differenceMinor ?? 0, shown.currency),
						})}
					</p>
				</section>
			)}

			{phase !== "waiting" && (
				<>
					<Button
						variant="primary"
						size="lg"
						disabled={gate !== "ok"}
						loading={start.isPending}
						onClick={() => start.mutate()}
					>
						{t("driver.handover.start")}
					</Button>
					{gate === "offline" && (
						<p className="text-[13px] text-fg-muted">{t("driver.handover.offline")}</p>
					)}
					{gate === "empty" && (
						<p className="text-[13px] text-fg-muted">{t("driver.handover.empty")}</p>
					)}
					{refused !== null && (
						<p role="alert" className="text-[13px] text-danger-fg">
							{t(REFUSALS[refused] ?? "driver.handover.failed")}
						</p>
					)}
				</>
			)}

			<section className="flex flex-col gap-2">
				<h2 className="text-[15px] font-bold text-fg">{t("driver.handover.history")}</h2>
				{!online ? (
					<p className="text-[14px] text-fg-muted">{t("driver.handover.historyOffline")}</p>
				) : (history.data?.items.length ?? 0) === 0 ? (
					<p className="text-[14px] text-fg-muted">{t("driver.handover.historyEmpty")}</p>
				) : (
					<ul className="flex flex-col divide-y divide-border card">
						{history.data?.items.map((h: CodHandover) => (
							<li
								key={h.handoverId}
								className="flex flex-wrap items-center gap-2 px-4 py-3 text-[14px]"
							>
								<span className="me-auto text-fg">
									{formatDateTime(h.createdAt, { locale, timeZone })}
								</span>
								<span className="font-bold text-fg">{money(h.expectedTotalMinor, h.currency)}</span>
								<StatusChip tone={PHASE_TONE[phaseOf(h)]} size="sm">
									{t(`driver.handover.phase.${phaseOf(h)}`)}
								</StatusChip>
							</li>
						))}
					</ul>
				)}
			</section>
		</div>
	);
}
